<template>
  <div
    class="relative h-full overflow-x-hidden overflow-y-scroll rounded-md bg-white p-6"
  >
    <h4
      class="sticky top-0 bg-white pb-2 text-2xs font-bold uppercase text-body-color"
    >
      Preventable fatal crashes with citation:
      <span>{{ insight[associatedItems]?.length }}</span>
    </h4>
    <div class="mt-4 flex flex-col gap-4 rounded-md">
      <div
        v-for="(item, index) in insight[associatedItems]"
        :key="`${item.id}-${index}`"
        class="rounded-md border-2 border-red-500 px-3 py-2"
      >
        <template v-if="item.vehicle_id">
          <!-- <router-link
            :to="{ name: 'VehicleShow', params: { id: item.vehicle_id } }"
            target="_blank"
            class="text-xl text-red-500 no-underline"
          > -->
          <div class="flex flex-col items-start gap-3 leading-snug">
            <div class="flex flex-col gap-0.5">
              <div class="mt-2 flex items-center gap-4 underline">
                {{ item.vin }}
                <div class="flex items-center">
                  <custom-badge
                    bg-color-class="bg-red-500"
                    color-class="text-white"
                    size="md"
                    text="Preventable fatal crash with citation"
                    icon="exclamation"
                  />
                </div>
              </div>
              <div class="text-xs uppercase">
                {{ insight?.associated_carriers?.[0]?.name }}
              </div>
            </div>
            <div
              class="grid w-full grid-cols-6 grid-rows-2 justify-start gap-x-8 gap-y-2"
            >
              <div class="col-span-1 row-span-2">
                <div class="border-2 border-transparent">
                  <h4
                    class="mb-1 text-2xs font-bold uppercase text-gray-over-dark"
                  >
                    <div class="pt-1">Fatalities</div>
                  </h4>
                  <div class="inline-block text-xs text-dark-gray">
                    <div
                      class="flex flex-row flex-wrap items-center justify-center gap-1"
                    >
                      <div
                        v-for="idx in item?.fatalities"
                        :key="`fatality-${idx}`"
                        class="h-12 w-auto text-dark-gray"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          class="h-full w-full text-dark-gray"
                          xmlns:ns="&amp;#38;ns_ai;"
                          xml:space="preserve"
                          version="1.1"
                          x="0px"
                          y="0px"
                          enable-background="new 0 0 124.189 132.243"
                          viewBox="40 0 44.189 132.243"
                        >
                          <g ns:extraneous="self">
                            <path
                              stroke-width="1.25"
                              fill="currentColor"
                              d="m62.096 8.5859c-5.208 0-9.424 4.2191-9.424 9.4261 0.001 5.203 4.217 9.424 9.424 9.424 5.202 0 9.422-4.221 9.422-9.424 0-5.208-4.22-9.4261-9.422-9.4261zm-10.41 21.268c-6.672 0-12.131 5.407-12.131 12.07v29.23c0 2.275 1.791 4.123 4.07 4.123 2.28 0 4.127-1.846 4.127-4.123v-26.355h2.102s0.048 68.811 0.048 73.331c0 3.05 2.478 5.53 5.532 5.53 3.052 0 5.525-2.48 5.525-5.53v-42.581h2.27v42.581c0 3.05 2.473 5.53 5.531 5.53 3.054 0 5.549-2.48 5.549-5.53v-73.331h2.127v26.355c0 2.275 1.85 4.123 4.126 4.123 2.28 0 4.073-1.846 4.073-4.123v-29.23c0-6.663-5.463-12.07-12.129-12.07h-20.82z"
                            />
                          </g>
                        </svg>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div class="col-span-1">
                <div class="border-2 border-transparent">
                  <h4
                    class="mb-1 text-2xs font-bold uppercase text-gray-over-dark"
                  >
                    <div class="pt-1">Report Date</div>
                  </h4>
                  <div
                    v-if="item?.report_date"
                    class="text-2xs text-dark-gray"
                  >
                    <div>{{ item.report_date }}</div>
                  </div>
                  <div
                    v-else
                    class="text-2xs text-dark-gray"
                  >
                    <div>-</div>
                  </div>
                </div>
              </div>
              <div class="col-span-1">
                <div class="border-2 border-transparent">
                  <h4
                    class="mb-1 text-2xs font-bold uppercase text-gray-over-dark"
                  >
                    <div class="pt-1">State</div>
                  </h4>
                  <div
                    v-if="item?.report_state"
                    class="text-2xs text-dark-gray"
                  >
                    <div>{{ item?.report_state }}</div>
                  </div>
                  <div
                    v-else
                    class="text-2xs text-dark-gray"
                  >
                    <div>-</div>
                  </div>
                </div>
              </div>
              <div class="col-span-1">
                <div class="border-2 border-transparent">
                  <h4
                    class="mb-1 text-2xs font-bold uppercase text-gray-over-dark"
                  >
                    <div class="pt-1">Report #</div>
                  </h4>
                  <div
                    v-if="item?.report_number"
                    class="text-2xs text-dark-gray"
                  >
                    <div>{{ item?.report_number }}</div>
                  </div>
                  <div
                    v-else
                    class="text-2xs text-dark-gray"
                  >
                    <div>-</div>
                  </div>
                </div>
              </div>
              <div class="col-span-2">
                <div class="border-2 border-transparent">
                  <h4
                    class="mb-1 text-2xs font-bold uppercase text-gray-over-dark"
                  >
                    <div class="pt-1">VIN</div>
                  </h4>
                  <div
                    v-if="item?.vin"
                    class="text-2xs text-dark-gray"
                  >
                    <div>{{ item?.vin }}</div>
                  </div>
                  <div
                    v-else
                    class="text-2xs text-dark-gray"
                  >
                    <div>-</div>
                  </div>
                </div>
              </div>
              <div class="col-span-1">
                <div class="border-2 border-transparent">
                  <h4
                    class="mb-1 text-2xs font-bold uppercase text-gray-over-dark"
                  >
                    <div class="pt-1">Plate #</div>
                  </h4>
                  <div
                    v-if="item?.vehicle_license_number"
                    class="text-2xs text-dark-gray"
                  >
                    <div>
                      {{ item?.vehicle_license_number
                      }}<span
                        v-if="item?.vehicle_license_state"
                        class="ml-0.5"
                        >({{ item?.vehicle_license_state }})</span
                      >
                    </div>
                  </div>
                  <div
                    v-else
                    class="text-2xs text-body-color"
                  >
                    <div>-</div>
                  </div>
                </div>
              </div>
              <div class="col-span-1">
                <div class="border-2 border-transparent">
                  <h4
                    class="mb-1 text-2xs font-bold uppercase text-gray-over-dark"
                  >
                    <div class="pt-1">Injuries</div>
                  </h4>
                  <div
                    v-if="item?.injuries"
                    class="text-2xs text-dark-gray"
                  >
                    <div>{{ item?.injuries }}</div>
                  </div>
                  <div
                    v-else
                    class="text-2xs text-body-color"
                  >
                    <div>-</div>
                  </div>
                </div>
              </div>
              <div class="col-span-1">
                <div class="border-2 border-transparent">
                  <h4
                    class="mb-1 text-2xs font-bold uppercase text-gray-over-dark"
                  >
                    <div class="pt-1">Conditions</div>
                  </h4>
                  <div class="text-2xs text-dark-gray">
                    <div
                      v-if="
                        item?.road_surface_condition_description ||
                        item?.weather_condition_description ||
                        item?.light_condition_description
                      "
                      class="text-2xs text-dark-gray"
                    >
                      <div>{{ conditionText(item) }}</div>
                    </div>
                    <div
                      v-else
                      class="text-2xs text-dark-gray"
                    >
                      <div>-</div>
                    </div>
                  </div>
                </div>
              </div>
              <div class="col-span-2">
                <div class="border-2 border-transparent">
                  <h4
                    class="mb-1 text-2xs font-bold uppercase text-gray-over-dark"
                  >
                    <div class="pt-1">Trafficway</div>
                  </h4>
                  <div class="text-2xs text-dark-gray">
                    <div
                      v-if="item?.trafficway_description !== null"
                      class="text-2xs text-dark-gray"
                    >
                      <div>{{ item?.trafficway_description }}</div>
                    </div>
                    <div
                      v-else
                      class="text-2xs text-dark-gray"
                    >
                      <div>-</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <!-- </router-link> -->
        </template>
        <template v-else>
          <div class="flex flex-col items-start gap-3 leading-snug">
            <div class="flex flex-col gap-0.5">
              <div class="mt-2 flex items-center gap-4">
                <p class="underline">
                  {{ item.vin }}
                </p>
                <div class="flex items-center">
                  <custom-badge
                    bg-color-class="bg-red-500"
                    color-class="text-white"
                    size="md"
                    text="Preventable fatal crash with citation"
                    icon="exclamation"
                  />
                </div>
              </div>
              <div class="text-xs uppercase">
                {{ insight?.associated_carriers?.[0]?.name }}
              </div>
            </div>
            <div
              class="grid w-full grid-cols-6 grid-rows-2 justify-start gap-x-8 gap-y-2"
            >
              <div class="col-span-1 row-span-2">
                <div class="border-2 border-transparent">
                  <h4
                    class="mb-1 text-2xs font-bold uppercase text-gray-over-dark"
                  >
                    <div class="pt-1">Fatalities</div>
                  </h4>
                  <div class="inline-block text-xs text-dark-gray">
                    <div
                      class="flex flex-row flex-wrap items-center justify-center gap-1"
                    >
                      <div
                        v-for="idx in item?.fatalities"
                        :key="`fatality-${idx}`"
                        class="h-12 w-auto text-dark-gray"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          class="h-full w-full text-dark-gray"
                          xmlns:ns="&amp;#38;ns_ai;"
                          xml:space="preserve"
                          version="1.1"
                          x="0px"
                          y="0px"
                          enable-background="new 0 0 124.189 132.243"
                          viewBox="40 0 44.189 132.243"
                        >
                          <g ns:extraneous="self">
                            <path
                              stroke-width="1.25"
                              fill="currentColor"
                              d="m62.096 8.5859c-5.208 0-9.424 4.2191-9.424 9.4261 0.001 5.203 4.217 9.424 9.424 9.424 5.202 0 9.422-4.221 9.422-9.424 0-5.208-4.22-9.4261-9.422-9.4261zm-10.41 21.268c-6.672 0-12.131 5.407-12.131 12.07v29.23c0 2.275 1.791 4.123 4.07 4.123 2.28 0 4.127-1.846 4.127-4.123v-26.355h2.102s0.048 68.811 0.048 73.331c0 3.05 2.478 5.53 5.532 5.53 3.052 0 5.525-2.48 5.525-5.53v-42.581h2.27v42.581c0 3.05 2.473 5.53 5.531 5.53 3.054 0 5.549-2.48 5.549-5.53v-73.331h2.127v26.355c0 2.275 1.85 4.123 4.126 4.123 2.28 0 4.073-1.846 4.073-4.123v-29.23c0-6.663-5.463-12.07-12.129-12.07h-20.82z"
                            />
                          </g>
                        </svg>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div class="col-span-1">
                <div class="border-2 border-transparent">
                  <h4
                    class="mb-1 text-2xs font-bold uppercase text-gray-over-dark"
                  >
                    <div class="pt-1">Report Date</div>
                  </h4>
                  <div
                    v-if="item?.report_date"
                    class="text-2xs text-dark-gray"
                  >
                    <div>{{ item.report_date }}</div>
                  </div>
                  <div
                    v-else
                    class="text-2xs text-dark-gray"
                  >
                    <div>-</div>
                  </div>
                </div>
              </div>
              <div class="col-span-1">
                <div class="border-2 border-transparent">
                  <h4
                    class="mb-1 text-2xs font-bold uppercase text-gray-over-dark"
                  >
                    <div class="pt-1">State</div>
                  </h4>
                  <div
                    v-if="item?.report_state"
                    class="text-2xs text-dark-gray"
                  >
                    <div>{{ item?.report_state }}</div>
                  </div>
                  <div
                    v-else
                    class="text-2xs text-dark-gray"
                  >
                    <div>-</div>
                  </div>
                </div>
              </div>
              <div class="col-span-1">
                <div class="border-2 border-transparent">
                  <h4
                    class="mb-1 text-2xs font-bold uppercase text-gray-over-dark"
                  >
                    <div class="pt-1">Report #</div>
                  </h4>
                  <div
                    v-if="item?.report_number"
                    class="text-2xs text-dark-gray"
                  >
                    <div>{{ item?.report_number }}</div>
                  </div>
                  <div
                    v-else
                    class="text-2xs text-dark-gray"
                  >
                    <div>-</div>
                  </div>
                </div>
              </div>
              <div class="col-span-2">
                <div class="border-2 border-transparent">
                  <h4
                    class="mb-1 text-2xs font-bold uppercase text-gray-over-dark"
                  >
                    <div class="pt-1">VIN</div>
                  </h4>
                  <div
                    v-if="item?.vin"
                    class="text-2xs text-dark-gray"
                  >
                    <div>{{ item?.vin }}</div>
                  </div>
                  <div
                    v-else
                    class="text-2xs text-dark-gray"
                  >
                    <div>-</div>
                  </div>
                </div>
              </div>
              <div class="col-span-1">
                <div class="border-2 border-transparent">
                  <h4
                    class="mb-1 text-2xs font-bold uppercase text-gray-over-dark"
                  >
                    <div class="pt-1">Plate #</div>
                  </h4>
                  <div
                    v-if="item?.vehicle_license_number"
                    class="text-2xs text-dark-gray"
                  >
                    <div>
                      {{ item?.vehicle_license_number
                      }}<span
                        v-if="item?.vehicle_license_state"
                        class="ml-0.5"
                        >({{ item?.vehicle_license_state }})</span
                      >
                    </div>
                  </div>
                  <div
                    v-else
                    class="text-2xs text-body-color"
                  >
                    <div>-</div>
                  </div>
                </div>
              </div>
              <div class="col-span-1">
                <div class="border-2 border-transparent">
                  <h4
                    class="mb-1 text-2xs font-bold uppercase text-gray-over-dark"
                  >
                    <div class="pt-1">Injuries</div>
                  </h4>
                  <div
                    v-if="item?.injuries"
                    class="text-2xs text-dark-gray"
                  >
                    <div>{{ item?.injuries }}</div>
                  </div>
                  <div
                    v-else
                    class="text-2xs text-body-color"
                  >
                    <div>-</div>
                  </div>
                </div>
              </div>
              <div class="col-span-1">
                <div class="border-2 border-transparent">
                  <h4
                    class="mb-1 text-2xs font-bold uppercase text-gray-over-dark"
                  >
                    <div class="pt-1">Conditions</div>
                  </h4>
                  <div class="text-2xs text-dark-gray">
                    <div
                      v-if="
                        item?.road_surface_condition_description ||
                        item?.weather_condition_description ||
                        item?.light_condition_description
                      "
                      class="text-2xs text-dark-gray"
                    >
                      <div>{{ conditionText(item) }}</div>
                    </div>
                    <div
                      v-else
                      class="text-2xs text-dark-gray"
                    >
                      <div>-</div>
                    </div>
                  </div>
                </div>
              </div>
              <div class="col-span-2">
                <div class="border-2 border-transparent">
                  <h4
                    class="mb-1 text-2xs font-bold uppercase text-gray-over-dark"
                  >
                    <div class="pt-1">Trafficway</div>
                  </h4>
                  <div class="text-2xs text-dark-gray">
                    <div
                      v-if="item?.trafficway_description !== null"
                      class="text-2xs text-dark-gray"
                    >
                      <div>{{ item?.trafficway_description }}</div>
                    </div>
                    <div
                      v-else
                      class="text-2xs text-dark-gray"
                    >
                      <div>-</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </template>
      </div>
    </div>
    <div v-if="insight.is_type === 'associated_digital_activity'">
      <template
        v-for="(carrier, index) in digitalInsights()"
        :key="index"
      >
        <div
          class="mb-3 flex flex-col items-start gap-3 rounded-md border border-gray-over-dark px-3 py-2"
        >
          <div class="mt-2 flex items-center gap-4">
            <router-link
              :to="{ name: 'CarriersShow', params: { id: carrier.id } }"
              target="_blank"
              class="mr-3 text-xl text-yellow-600 underline"
            >
              {{ capitalizeWords(carrier.name) }}
            </router-link>
            <div class="flex items-center">
              <custom-badge
                :bg-color-class="ratingBgColorClass(carrier.assessment_rating)"
                color-class="text-white"
                size="lg"
                :text="carrier.assessment_rating"
              />
            </div>
          </div>

          <div class="flex flex-row">
            <div class="mr-10">
              <h4 class="mb-1 text-2xs font-bold uppercase text-gray-over-dark">
                <div class="pt-1">address</div>
              </h4>
              <h4
                v-if="carrier.physical_addresses"
                class="-mt-1 text-2xs text-body-color"
              >
                {{
                  `${capitalizeWords(
                    carrier.physical_addresses[0]?.street1
                  )}, ${capitalizeWords(
                    carrier.physical_addresses[0]?.city
                  )}, ${capitalizeWords(
                    carrier.physical_addresses[0]?.state
                  )}, ${capitalizeWords(
                    carrier.physical_addresses[0]?.postal_code
                  )}`
                }}
              </h4>
            </div>
            <div>
              <h4 class="mb-1 text-2xs font-bold uppercase text-gray-over-dark">
                <div class="pt-1">authority</div>
              </h4>
              <h4 class="-mt-1 text-2xs text-body-color">
                {{
                  getCommonAuthority(carrier)
                    ? getCommonAuthority(carrier)
                    : '-'
                }}
              </h4>
            </div>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import CustomBadge from '@/components/CustomBadge.vue';
// import InfoTooltip from "@/components/InfoTooltip.vue";

export default {
  name: 'FatalCrashWithCitation',
  components: {
    CustomBadge,
  },
  props: {
    insight: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      // associatedCarrierData: {},
      reusedData: {
        phone: '',
        email: '',
        contact: '',
        address: {
          street1: '',
          street2: '',
          city: '',
          state: '',
          postal_code: '',
        },
      },
      matchingVehicles: [],
    };
  },
  computed: {
    associatedItems() {
      switch (this.insight?.is_type) {
        case 'reused_email_address':
          return 'associated_email_addresses';
        case 'reused_phone':
          return 'associated_phones';
        case 'reused_contact':
          return 'associated_contacts';
        case 'reused_address':
          return 'associated_addresses';
        case 'carrier_identifier_changed':
          return 'associated_carriers';
        case 'equipment_not_listed_on_scheduled_auto':
          return 'associated_vehicles';
        case 'fatal_crash_with_citation':
          return 'associated_sms_crashes';
        default:
          return null;
      }
    },
    associatedCarriers() {
      return this.insight.associated_carriers;
    },

    reusedEmailAddresses() {
      var emails = [];
      if (this.insight?.is_type == 'reused_email_address') {
        this.insight.associated_email_addresses.forEach(function (email) {
          emails.push(email.value);
        });
      } else {
        this.allInsights.forEach(function (item) {
          if (item?.is_type == 'reused_email_address') {
            item.associated_email_addresses.forEach(function (email) {
              emails.push(email.value);
            });
          }
        });
      }
      return emails;
    },
    reusedPhones() {
      var phones = [];
      if (this.insight?.is_type == 'reused_phone') {
        this.insight.associated_phones.forEach(function (phone) {
          phones.push(phone.phone_e164);
        });
      } else {
        this.allInsights.forEach(function (item) {
          if (item?.is_type == 'reused_phone') {
            item.associated_phones.forEach(function (phone) {
              phones.push(phone.phone_e164);
            });
          }
        });
      }
      return phones;
    },
    reusedContacts() {
      var contacts = [];
      if (this.insight?.is_type == 'reused_contact') {
        this.insight.associated_contacts.forEach(function (contact) {
          contacts.push(contact.name);
        });
      } else {
        this.allInsights.forEach(function (item) {
          if (item?.is_type == 'reused_contact') {
            item.associated_contacts.forEach(function (contact) {
              contacts.push(contact.name);
            });
          }
        });
      }
      return contacts;
    },
    reusedAddresses() {
      var addresses = [];
      if (this.insight?.is_type == 'reused_address') {
        this.insight.associated_carriers.forEach(function (aCarrier) {
          addresses.push(
            aCarrier.street1 +
              ', ' +
              aCarrier.city +
              ', ' +
              aCarrier.state +
              ' ' +
              aCarrier.postal_code
          );
        });
      } else {
        this.allInsights.forEach(function (item) {
          if (item?.is_type == 'reused_address') {
            item.associated_carriers.forEach(function (aCarrier) {
              addresses.push(
                aCarrier.street1 +
                  ', ' +
                  aCarrier.city +
                  ', ' +
                  aCarrier.state +
                  ' ' +
                  aCarrier.postal_code
              );
            });
          }
        });
      }
      return addresses;
    },
    allInsights() {
      return this.$store.state.carriers.showCarrierInsights.insights;
    },
    currentCarrier() {
      var object = {
        address: {},
      };
      var self = this;

      // if (this.insight?.is_type == 'reused_phone') {
      //   if (this.reusedPhones.includes(this.$store.state.carriers.showCarrier.dispatch_contact.phone)) {
      //     object.phone = this.$store.state.carriers.showCarrier.dispatch_contact.phone;
      //   } else {
      //     this.$store.state.carriers.showCarrier.contact_information.phones.forEach(function(phone) {
      //       if (self.reusedPhones.includes(phone.value)) {
      //         object.phone = phone.value;
      //       }
      //     });
      //   }
      // } else if (this.$store.state.carriers.showCarrier.dispatch_contact.phone) {
      //   object.phone = this.$store.state.carriers.showCarrier.dispatch_contact.phone;
      // } else {
      //   var censusMainPhone = 0;
      //   this.$store.state.carriers.showCarrier.contact_information.phones.forEach(function(phone) {
      //     if (!censusMainPhone && phone.is_type == 'census_main') {
      //       censusMainPhone = phone.value;
      //       object.phone = phone.value;
      //     }
      //   });
      // }
      // if (this.insight?.is_type == 'reused_email_address') {
      //   if (this.reusedEmailAddresses.includes(this.$store.state.carriers.showCarrier.dispatch_contact.email_address)) {
      //     object.email = this.$store.state.carriers.showCarrier.dispatch_contact.email_address;
      //   } else {
      //     this.$store.state.carriers.showCarrier.contact_information.email_addresses.forEach(function(email) {
      //       if (self.reusedEmailAddresses.includes(email.value)) {
      //         object.email = email.value;
      //       }
      //     });
      //   }
      // } else if (this.$store.state.carriers.showCarrier.dispatch_contact.email_address) {
      //   object.email = this.$store.state.carriers.showCarrier.dispatch_contact.email_address;
      // } else {
      //   var censusMainEmail = '';
      //   this.$store.state.carriers.showCarrier.contact_information.email_addresses.forEach(function(email) {
      //     if (!censusMainEmail && email.is_type == 'census') {
      //       censusMainEmail = email.value;
      //       object.email = email.value;
      //     }
      //   });
      // }
      // if (this.insight?.is_type == 'reused_contact') {
      //   if (this.reusedContacts.includes(this.$store.state.carriers.showCarrier.dispatch_contact.name)) {
      //     object.contact = this.$store.state.carriers.showCarrier.dispatch_contact.name;
      //   } else {

      //   }
      // } else if (this.$store.state.carriers.showCarrier.dispatch_contact.name) {
      //   object.contact = this.$store.state.carriers.showCarrier.dispatch_contact.name;
      // } else {
      //   var censusMainContact = '';
      //   this.$store.state.carriers.showCarrier.contact_information.contacts.forEach(function(contact) {
      //     if (!censusMainContact && contact.is_type == 'census') {
      //       censusMainContact = contact.name;
      //       object.contact = contact.name;
      //     }
      //   });
      // }
      this.$store.state.carriers.showCarrier.contact_information.phones.forEach(
        function (phone) {
          if (self.reusedPhones.includes(phone.phone_e164)) {
            object.phone = phone.value;
            self.reusedData.phone = phone.value;
          }
        }
      );

      this.$store.state.carriers.showCarrier.contact_information.email_addresses.forEach(
        function (email) {
          if (self.reusedEmailAddresses.includes(email.value)) {
            object.email = email.value;
            self.reusedData.email = email.value;
          }
        }
      );

      this.$store.state.carriers.showCarrier.contact_information.contacts.forEach(
        function (contact) {
          if (self.reusedContacts.includes(contact.name)) {
            object.contact = contact.name;
            self.reusedData.contact = contact.name;
          }
        }
      );

      this.$store.state.carriers.showCarrier.contact_information.addresses.forEach(
        function (cAddress) {
          if (
            self.reusedAddresses.includes(
              cAddress?.street1 +
                ', ' +
                cAddress?.city +
                ', ' +
                cAddress?.state +
                ' ' +
                cAddress?.postal_code
            )
          ) {
            console.log('OPTION 1');
            object.address.street1 = cAddress?.street1;
            object.address.street2 = cAddress?.street2;
            object.address.city = cAddress?.city;
            object.address.state = cAddress?.state;
            object.address.postal_code = cAddress?.postal_code;
            self.reusedData.address.street1 = cAddress?.street1;
            self.reusedData.address.street2 = cAddress?.street2;
            self.reusedData.address.city = cAddress?.city;
            self.reusedData.address.state = cAddress?.state;
            self.reusedData.address.postal_code = cAddress?.postal_code;
          } else {
            console.log('OPTION 2');
            object.address.street1 =
              self.$store.state.carriers.showCarrier?.contact_information?.headquarters?.address?.street1;
            object.address.street2 =
              self.$store.state.carriers.showCarrier?.contact_information?.headquarters?.address?.street2;
            object.address.city =
              self.$store.state.carriers.showCarrier?.contact_information?.headquarters?.address?.city;
            object.address.state =
              self.$store.state.carriers.showCarrier?.contact_information?.headquarters?.address?.state;
            object.address.postal_code =
              self.$store.state.carriers.showCarrier?.contact_information?.headquarters?.address?.postal_code;
            self.reusedData.address.street1 =
              self.$store.state.carriers.showCarrier?.contact_information?.headquarters?.address?.street1;
            self.reusedData.address.street2 =
              self.$store.state.carriers.showCarrier?.contact_information?.headquarters?.address?.street2;
            self.reusedData.address.city =
              self.$store.state.carriers.showCarrier?.contact_information?.headquarters?.address?.city;
            self.reusedData.address.state =
              self.$store.state.carriers.showCarrier?.contact_information?.headquarters?.address?.state;
            self.reusedData.address.postal_code =
              self.$store.state.carriers.showCarrier?.contact_information?.headquarters?.address?.postal_code;
          }
        }
      );

      return object;
    },
  },
  mounted() {
    if (this.insight?.is_type == 'equipment_not_listed_on_scheduled_auto') {
      this.getMatchingVehicles(this.insight.associated_vehicles);
    }
  },
  methods: {
    conditionText(item) {
      let arr = [];
      arr.push(item?.road_surface_condition_description);
      arr.push(item?.weather_condition_description);
      arr.push(item?.light_condition_description);
      return arr.join(', ');
    },
    classType: function (vehicle) {
      const weightClass = vehicle?.weight_class;
      switch (true) {
        case weightClass == null:
          return '-';
        case weightClass.includes('Class 1'):
          return 'Class 1';
        case weightClass.includes('Class 2'):
          return 'Class 2';
        case weightClass.includes('Class 3'):
          return 'Class 3';
        case weightClass.includes('Class 4'):
          return 'Class 4';
        case weightClass.includes('Class 5'):
          return 'Class 5';
        case weightClass.includes('Class 6'):
          return 'Class 6';
        case weightClass.includes('Class 7'):
          return 'Class 7';
        case weightClass.includes('Class 8'):
          return 'Class 8';
        default:
          return '-';
      }
    },
    digitalInsights() {
      let insights = [];
      this.allInsights.forEach((insight) => {
        if (insight.is_type === 'associated_digital_activity') {
          insights = insight.associated_carriers;
        }
      });
      return insights;
    },
    getAuthoritiesCurrentCarrier(carrier) {
      if (carrier.broker_authority_status) {
        return 'Broker';
      }
      if (carrier.common_authority_status) {
        return 'Common';
      }
      if (carrier.contract_authority_status) {
        return 'Contract';
      }
    },
    getCommonAuthority(carrier) {
      if (carrier.is_broker_authority) {
        return 'Broker';
      }
      if (carrier.is_common_authority) {
        return 'Common';
      }
      if (carrier.is_contract_authority) {
        return 'Contract';
      }
    },
    getAssociatedCarrierData(key, carrierId) {
      var returned = '';
      this.insight.associated_carriers.forEach(function (item) {
        if (item.id == carrierId) {
          returned = item[key];
          return returned;
        }
      });
      return returned;
    },
    getCarrierId(id1, id2) {
      if (id1 == undefined) {
        return id2;
      }
      return id1;
    },
    getMatchingVehicles(aVehicles) {
      if (this.insight?.is_type === 'associated_digital_activity') {
        const carrier = this.$store.state.carriers?.showCarrier;
        const currentCarrier = [
          {
            rating: carrier?.authority_assessment.rating,
            address: carrier?.contact_information?.headquarters?.address,
            authority: carrier?.authority.authorities,
          },
        ];
        this.matchingVehicles = currentCarrier;
      } else {
        var plates = [];
        this.$store.state.carriers.showCarrierEquipment.rows.forEach(
          function (vehicle) {
            aVehicles.forEach(function (aVehicle) {
              if (
                vehicle.latest_carrier_vehicle.license_plate_number ==
                aVehicle.license_plate_number
              ) {
                if (vehicle.vin == aVehicle.vin) {
                  plates.push({
                    id: vehicle.id,
                    is_type: vehicle.is_type,
                    vin: vehicle.vin,
                    reg_state:
                      vehicle.latest_carrier_vehicle.license_plate_state,
                    plate: vehicle.latest_carrier_vehicle.license_plate_number,
                  });
                }
              }
            });
          }
        );
        const seen = new Set();
        var filteredPlates = plates.filter((el) => {
          var reused = seen.has(el.id);
          seen.add(el.id);
          return !reused;
        });
        this.matchingVehicles = filteredPlates;
      }
    },
    ratingBgColorClass: function (rating) {
      switch (rating) {
        case 'Not Authorized':
          return 'bg-red-500';
        case 'Review Required':
          return 'bg-red-500';
        case 'Acceptable':
          return 'bg-green-500';
        case 'Authorized':
          return 'bg-green-500';
        case 'Authorized Carrier':
          return 'bg-green-500';
        case 'Authorized Broker':
          return 'bg-green-500';
        case 'Authorized Broker/Carrier':
          return 'bg-green-500';
        case 'Unrated':
          return 'bg-body-color';
        default:
          return 'bg-body-color';
      }
    },
    iconColorClass: function (rating) {
      switch (rating) {
        case true:
          return 'text-green-500';
        case false:
          return 'text-red-500';
        default:
          return 'text-body-color';
      }
    },
    wrapClass: function (rating) {
      switch (rating) {
        case true:
          return 'border-green-500';
        case false:
          return 'border-red-500';
        default:
          return 'border-light-gray';
      }
    },
    capitalizeWords: function (str) {
      if (str && typeof str === 'string' && str.length > 0) {
        return str
          .replace('_', ' ')
          .toLowerCase()
          .replace(/(^\w|\s\w)/g, (m) => m.toUpperCase())
          .replace('Llc', 'LLC')
          .replace('Dba', 'DBA');
      }
    },
    // aggregateStreet1: function() {
    //   var aggregated = [];
    //   this.insight.associated_addresses.forEach(function(el) {
    //     aggregated.push(el.street1);
    //   });
    //   return aggregated;
    // },
    // aggregatedMatch: function(address) {
    //   var match = false;
    //   this.insight.associated_addresses.forEach(function(el) {
    //     if (el.street1 == address.street1) {
    //       match = true;
    //     }
    //   });
    //   return match;
    // },
    isTypeLabel: function () {
      switch (this.insight?.is_type) {
        case 'reused_email_address':
          return 'Email Addresses';
        case 'reused_phone':
          return 'Phone Numbers';
        case 'reused_contact':
          return 'Contacts';
        case 'reused_address':
          return 'Addresses';
        case 'carrier_identifier_changed':
          return 'Carrier ID';
        case 'equipment_not_listed_on_scheduled_auto':
          return 'Equipment - Same License Plate';
        case 'associated_digital_activity':
          return 'Associated carriers';
      }
    },
  },
};
</script>
